/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable prefer-arrow-callback */
/* eslint-disable @typescript-eslint/naming-convention */
import React, { useMemo, useState, useEffect, useRef, useCallback, useImperativeHandle } from "react";
import classNames from "classnames";
import moment, { Moment } from "moment";
import { ControlledProps, useDefaultValue } from "tea-component/es/form/controlled";
import CalendarPart from "tea-component/es/calendar/CalendarPart";
// import { CalendarTable, getTimeRange } from "tea-component/es/calendar/CalendarTable";
import { CalendarTable, getTimeRange } from "./CalendarTable";
import { CalendarWrapper } from "tea-component/es/datepicker/CalendarWrapper";
import { Input } from "tea-component/es/input/Input";
import { Locale, useTranslation } from "tea-component/es/i18n";
import { CommonDatePickerProps } from "tea-component/es/datepicker/DatePickerProps";
import {
  getYearMonthDate,
  DatePickerTrigger,
  isSame,
  isAfter,
  isBefore,
  isValidDate,
  isValidMonth,
} from "tea-component/es/datepicker/util";
import {
  genShowHourMinuteSecond,
  getHourMinuteSecond,
  getValidTimeValue,
  isValidTimeValue,
} from "tea-component/es/timepicker/util";
import { TimeDisabledProps } from "tea-component/es/timepicker/TimeProps";
import { DropdownBox } from "tea-component/es/dropdown";
import {
  RangeDateType,
  showTimeType,
  CalendarTableType,
  DateChangeContext,
} from "tea-component/es/calendar/DateProps";
import { useDefault } from "tea-component/es/_util/use-default";
import { Popover } from "tea-component/es/popover/Popover";
import { useConfig } from "tea-component/es/_util/config-context";
import { Icon } from "tea-component/es/icon";
import { noop } from "tea-component/es/_util/noop";
import { RangePickerContext } from "tea-component/es/datepicker/RangePickerContext";
import { forwardRefWithStatics } from "tea-component/es/_util/forward-ref-with-statics";
import { mergeEventProps } from "tea-component/es/_util/merge-event-props";
import { KeyMap } from "tea-component/es/_util/key-map";

type UserTranslationType = {[key in "en" | "cn"]: {
  [key: string]: string;
} }
export interface RangePickerProps
  extends Omit<CommonDatePickerProps, "placeholder">,
  ControlledProps<RangeDateType> {
  /**
   * label
   * @default 0
   */
  label?: React.ReactNode;
  /**
   * 是否为单周选择模式
   * @default false
   */
  singleWeek?: boolean;
  /**
   * 是否为单月选择模式
   * @default false
   */
  singleMonth?: boolean;
  /**
   * 是否为单选择模式
   * @default false
   */
  isSingleSelect?: boolean;
  /**
   * 星期几作为一周的第一天
   * @default 0
   */
  startOfWeek?: number;
  /**
   * 是否恢复当前左右面板的值
   * @default true
   */
  restoreView?: boolean;
  /**
   * 手动修改input，是否直接更新value
   * @default true
   */
  syncInput?: boolean;
  /**
   * 关闭弹窗时，是否修正时间
   * @default true
   */
  fixDate?: boolean;
  /**
   * 分隔符
   * @default ~
   */
  separator?: string;

  /**
   * placeholder
   * @default "选择日期" 或 “选择时间”（包含时间选择时）
   * @snice 2.7.7
   */
  placeholder?: string | [string, string];

  /**
   * 是否开启时间选择，可传递对象设定时间选择配置
   */
  showTime?: showTimeType<RangeDateType>;

  /**
   * 不可选的日期
   */
  disabledDate?: (date: Moment, startDate?: Moment) => boolean;

  /**
   * 不可选的月份
   */
  disabledMonth?: (date: Moment, startDate?: Moment) => boolean;

  /**
   * 不可选的季度
   */
  disabledQuarter?: (date: Moment, startDate?: Moment) => boolean;

  /**
   * 日历类型
   * @default date
   */
  calendarType?: "date" | "month" | "quarter" | "week";

  /**
   * 不可选的时间
   */
  disabledTime?: (
    dates: RangeDateType,
    partial: "start" | "end"
  ) => TimeDisabledProps;

  /**
   * header快捷按钮中，需要隐藏的快捷按钮，比如Last 7 Days、Year to Date 等快捷按钮
   */
  hiddenHeaderBtns: string[];

  /**
   * 用户自定义时间组件文案
   */
  userTranslation?: UserTranslationType;
  
  /**
   * 日期弹窗底部的内容, 传入的配置格式和 CalendarPart.Footer的参数保持一致
   */
  footerConfig?: { style?: React.CSSProperties; className?: string; left?: React.ReactNode; right?: React.ReactNode; bottom?: boolean; top?: boolean; };
  /**
   * 用户手动选择日期 变化后是否自动关闭弹窗, 默认为true 关闭弹窗
   */
  changeClose?: boolean;
}

interface RangeInputValue {
  inputValueStart: string;
  separator: string;
  inputValueEnd: string;
}

type InputValueType = RangeInputValue | string;

const getFormat = (showTime: any, calendarType: string) => {
  if (calendarType === "date") {
    return showTime ? "YYYY-MM-DD HH:mm:ss" : "YYYY-MM-DD";
  }

  if (calendarType === "week") {
    return "YYYY-MM-DD";
  }

  if (calendarType === "month") {
    return "YYYY-MM";
  }

  return "YYYY";
};

export function isValidRangeValue(value: any) {
  return (
    Array.isArray(value) &&
    moment.isMoment(value[0]) &&
    value[0].isValid() &&
    moment.isMoment(value[1]) &&
    value[1].isValid()
  );
}

export function getStartAndEndYear(date: Moment) {
  const year = date.year();
  const startYear = year - (year % 20);
  const endYear = year + 20 - 1;
  return {
    startYear,
    endYear
  };
}


function useUserTranslation({
  userTranslation,
}: {
  userTranslation?: UserTranslationType;
}) {
  function getUserTranslation(translation: Locale) {
    if (!userTranslation) return {};
    const isCn = ['zh_CN', 'zh'].includes(translation.locale);
    const key = isCn ? "cn" : "en";
    return userTranslation?.[key] || {};
  }
 
  const translation = useTranslation(moment);

  const translationResult = useMemo(() => Object.assign(translation, getUserTranslation(translation)), [translation]);

  return translationResult;
}

export const RangePicker = forwardRefWithStatics(
  function RangePicker(
    props: RangePickerProps,
    ref: React.Ref<HTMLDivElement>
  ) {
    const t = useUserTranslation({ userTranslation: props.userTranslation });
    moment.updateLocale(moment.locale(), {
      week: {
        dow: props.startOfWeek || 0, // 星期的第一天是星期一
        doy: 4  // 年份的第一周必须包含1月4日 (7 + 1 - 4)
      }
    });
    const { classPrefix } = useConfig();
    const {
      header,
      className,
      style,
      value,
      onChange,
      showTime,
      disabled,
      separator = "~",
      format: _format,
      placeholder = showTime ? t.selectTime : t.selectDate,
      defaultOpen = false,
      open,
      onOpenChange = noop,
      onInputValueChange = noop,
      placement = "bottom-start",
      placementOffset = 5,
      closeOnScroll = true,
      escapeWithReference,
      popupContainer,
      overlayClassName,
      overlayStyle,
      clearable,
      calendarType = "date",
      label = null,
      rangePrefix,
      onTimeChange,
      restoreView = true,
      syncInput = true,
      fixDate = true,
      autoRange,
      singleWeek = false,
      singleMonth = false,
      isSingleSelect = false,
      footerConfig,
      changeClose = true,
      overlay,
      popoverProps,
      ...restProps
    } = useDefaultValue(props as any, [null, null]) as any;
    // 是否手动修改input起始与结束时间
    const [inputChange, setInputChange] = useState(false);

    const parentUnit = ["month", "quarter", "year"].includes(calendarType)
      ? "year"
      : "month";

    const [hover, setHover] = useState<boolean>(false);

    const format = _format || getFormat(showTime, calendarType);

    // 当前面板类型
    const [type, setType] = useState<[CalendarTableType, CalendarTableType]>([
      calendarType,
      calendarType,
    ]);

    // 当前左/右面板展示时间
    const [curStartView, setStartCurView] = useState<Moment>(
      getDefaultViewMoment(0)
    );
    const [curEndView, setEndCurView] = useState<Moment>(
      getDefaultViewMoment(1)
    );

    // 当前选中日期
    const [curValue, setCurValue] = useState<RangeDateType>(
      isValidRangeValue(value)
        ? [value[0].clone(), value[1].clone()]
        : [null, null]
    );

    // 当前 hover 的日期
    const [hovered, setHovered] = useState<Moment | null>(null);

    // 上次选中日期
    const preValidValueRef = useRef<RangeDateType>(value || [null, null]);

    // 选择器是否展开
    const [active, setActive] = useDefault(open, defaultOpen, onOpenChange);

    /**
     * 是否为按range模式展示日期结果
     * range展示开始日期和结束日期，非range只展示一个日期
     * 时间粒度为周时，非range也是展示开始日期和结束日期
     */
    const isRangeLabel = useMemo(() => {
      const { length } = autoRange ?? {};
      return length !== 0 || calendarType === 'week';
    }, [autoRange?.length, calendarType]);

    // 对父组件暴露的方法
    // eslint-disable-next-line arrow-body-style
    useImperativeHandle(ref, () => ({
      //  关闭时间组件弹出窗
      setPopActive: () => {
        setActive(false);
      },
      changeType: (newType) => setType(newType),
      currentType: type,
    }));



    /** 是否显示毫秒 */
    const { showMilliseconds } = genShowHourMinuteSecond(format);

    // 输入框显示值
    const startInputRef = useRef<HTMLInputElement>(null);
    const endInputRef = useRef<HTMLInputElement>(null);
    // 2.7.4 后改为两个输入框显示
    const getInputValue = useCallback(
      (value: RangeDateType): InputValueType => {
        const [start, end] = value || [null, null];
        // if (moment.isMoment(start) && moment.isMoment(end)) {
        //   const startMoment = start.locale(t.locale);
        //   const endMoment = end.locale(t.locale);
        //
        //   if (calendarType === "quarter") {
        //     return {
        //       inputValueStart: `${startMoment.format(
        //         "YYYY"
        //       )}-Q${startMoment.quarter()}`,
        //       separator,
        //       inputValueEnd: `${endMoment.format(
        //         "YYYY"
        //       )}-Q${endMoment.quarter()}`,
        //     };
        //   }
        //
        //   return {
        //     inputValueStart: startMoment.format(format),
        //     separator,
        //     inputValueEnd: endMoment.format(format),
        //   };
        // }
        // return "";
        // 输入优化，选择时间时，同步显示在input框
        let startMoment: moment.Moment | null = null;
        let endMoment: moment.Moment | null = null;
        if (moment.isMoment(start)) {
          startMoment = start.locale(t.locale);
        }
        if (moment.isMoment(end)) {
          endMoment = end.locale(t.locale);
        }
        setTimeout(() => {
          if (startMoment && !endMoment) {
            endInputRef.current?.focus();
          }
        }, 0);
        if (calendarType === "quarter") {
          return {
            inputValueStart: startMoment ? `${startMoment.format(
              "YYYY"
            )}-Q${startMoment.quarter()}` : '',
            separator,
            inputValueEnd: endMoment ? `${endMoment.format(
              "YYYY"
            )}-Q${endMoment.quarter()}` : '',
          };
        }
        return {
          inputValueStart: startMoment ? startMoment.format(format) : '',
          separator,
          inputValueEnd: endMoment ? endMoment.format(format) : '',
        };
      },
      [calendarType, format, separator, t.locale]
    );
    const [inputValue, setInputValue] = useState<InputValueType>(
      getInputValue(curValue)
    );

    useEffect(() => {
      setStartCurView(getDefaultViewMoment(0));
      setEndCurView(getDefaultViewMoment(1));
    }, [props.range, calendarType]);

    useEffect(() => {
      setCurValue(
        isValidRangeValue(value)
          ? [value[0].clone(), value[1].clone()]
          : [null, null]
      );
      // [TODO] moment 更改后直接获取值（format）可能拿到是之前值？
      const timer = setTimeout(() => setInputValue(getInputValue(value)), 0);
      return () => {
        clearTimeout(timer);
      };
    }, [format, separator, value, getInputValue, active]);

    // 关闭弹窗时修正时间
    useEffect(() => {
      if (fixDate && !active) {
        const [start, end] = curValue;
        if (moment.isMoment(start) && !moment.isMoment(end)) {
          if (!autoRange) {
            if (calendarType === 'week') {
              onChange([moment(start).startOf('week'), moment(start).endOf('week')], {});
            } else {
              onChange([start, start], {});
            }
          }
        }
        // 弹窗关闭时，如何有手动输入日期，将input日期值传入onchange
        // (syncInput==false时，输入日期不会自动触发onChange，在关闭弹窗时才触发)
        if (inputChange && !syncInput && moment.isMoment(start) && moment.isMoment(end)) {
          onChange([start, end], {});
          setInputChange(false);
        }
      }
      // 激活面板时，重置当前CurView
      if (active) {
        // 设置焦点
        if (!curValue?.[0]) {
          startInputRef.current?.focus();
        }
        if (restoreView) {
          setStartCurView(getDefaultViewMoment(0));
          setEndCurView(getDefaultViewMoment(1));
        }
      }
    }, [active]);

    useEffect(() => {
      preValidValueRef.current = value || [null, null];
    }, [value]);

    const timerRef = useRef(null);
    useEffect(
      () => () => {
        clearTimeout(timerRef?.current as any);
      },
      []
    );

    useEffect(() => {
      if (calendarType === 'year') {
        return;
        // let [start = moment(), end = moment()] = curValue || [];
        // if (!start) {
        //   start = moment()
        // }
        // if (!end) {
        //   end = moment();
        // }
        //
        // const startYear = start.year();
        // const endYear = end.year();
        //
        // const cstart = startYear - (startYear % 20);
        // const cend = cstart + 20 - 1;
        //
        // return endYear > cend ? end : moment(end).add(20, parentUnit);
      }
      // 同一个月不跳转面板 | 同一个年不跳转面板
      // const limit = calendarType === "date" || calendarType === "week" ? "M" : "y";
      const limit = calendarType === "date" || calendarType === "week" ? "M" : "y";
      if (!curValue[0]?.isSame(curValue[1], limit) && curValue[1]) {
        setStartCurView(curValue[0]);
      }
      if (!curValue[1]?.isSame(curValue[0], limit) && curValue[1]) {
        setEndCurView(curValue[1]);
      }
    }, [calendarType, curValue]);

    function handleChange(
      value: RangeDateType,
      context: DateChangeContext
    ): void {
      // 经分时间对比页面点击日期自动补齐
      if (onTimeChange) {
        onTimeChange(value, context);
        return;
      }

      // 单周选择模式下，自动补齐一周的开始和结束时间
      // isSingleSelect开启单选择模式
      if ((isSingleSelect || (singleWeek && calendarType === 'week') || (singleMonth && calendarType === 'month')) && value?.[0] && !value?.[1]) {
        const start = moment(value[0]).startOf(calendarType);
        const end = moment(value[0]).endOf(calendarType);
        // 直接触发onChange并关闭弹窗
        onChange([start, end], context);
        changeClose && handleClose();
        return;
      }

      // 同步日期/时间
      if (showTime && isValidRangeValue(value)) {
        value = syncDate(value, context.type as any); // eslint-disable-line no-param-reassign
      }

      setCurValue(value);
      // [TODO] moment 更改后直接获取值（format）可能拿到是之前值？
      timerRef.current = setTimeout(
        () => setInputValue(getInputValue(value)),
        0
      );

      // 没有时间选择时没有二次确认选中, 日期改变不关闭弹窗时, 也没有二次确认选中
      if ((!showTime || !changeClose) && isValidRangeValue(value)) {
        onChange(value, context);
        changeClose && handleClose();
      }
    }

    function handleOk(event: React.KeyboardEvent<Element>): void {
      let value = curValue;
      if (isValidRangeValue(curValue) && isAfter(curValue[0], curValue[1])) {
        value = [curValue[1], curValue[0]];
        setCurValue(value);
      }
      onChange(value, { event });
      changeClose && handleClose();
    }

    function handleOpen(): void {
      if (disabled) {
        return;
      }
      setActive(true);
      setType([calendarType, calendarType]);
    }

    function handleClose(): void {
      setActive(false);
    }

    /**
     * 同步日期/时间
     * 如果当前修改为日期，则同步上次时间并修正；
     * 如果当前修改为时间，则同步上次日期；
     */
    function syncDate(
      value: RangeDateType,
      type: CalendarTableType
    ): RangeDateType {
      const preValidValue = preValidValueRef.current;
      const { range, disabledTime = () => ({}) } = props;

      // 如果包含上次选择，则以上次选择为基准同步
      if (isValidRangeValue(preValidValue)) {
        if (type === "date") {
          const timeStart = getValidTimeValue(preValidValue[0], {
            range: getTimeRange(value[0], range),
            ...disabledTime(value, "start"),
            hourStep: showTime?.hourStep || 1,
            minuteStep: showTime?.minuteStep || 1,
            secondStep: showTime?.secondStep || 1,
          });
          const timeEnd = getValidTimeValue(preValidValue[1], {
            range: getTimeRange(value[1], range),
            ...disabledTime(value, "end"),
            hourStep: showTime?.hourStep || 1,
            minuteStep: showTime?.minuteStep || 1,
            secondStep: showTime?.secondStep || 1,
          });
          value[0].set(getHourMinuteSecond(timeStart));
          value[1].set(getHourMinuteSecond(timeEnd));
        }
        if (type === "time") {
          value[0].set(getYearMonthDate(preValidValue[0]));
          value[1].set(getYearMonthDate(preValidValue[1]));
        }
        // 如果是首次选择，则将当前时间进行修正
      } else if (type === "date") {
        // 合并 showTime.defaultValue
        if (isValidRangeValue(showTime?.defaultValue)) {
          const [start, end] = showTime.defaultValue;
          value[0].set(getHourMinuteSecond(start));
          value[1].set(getHourMinuteSecond(end));
        }

        const timeStart = getValidTimeValue(value[0], {
          range: getTimeRange(value[0], range),
          ...disabledTime(value, "start"),
          hourStep: showTime?.hourStep || 1,
          minuteStep: showTime?.minuteStep || 1,
          secondStep: showTime?.secondStep || 1,
        });
        const timeEnd = getValidTimeValue(value[1], {
          range: getTimeRange(value[1], range),
          ...disabledTime(value, "end"),
          hourStep: showTime?.hourStep || 1,
          minuteStep: showTime?.minuteStep || 1,
          secondStep: showTime?.secondStep || 1,
        });

        value[0].set(getHourMinuteSecond(timeStart));
        value[1].set(getHourMinuteSecond(timeEnd));
      }

      preValidValueRef.current = value;
      return value;
    }

    /**
     * 获取 ShowTime 参数
     * @param index 左/右面板 - 0/1
     */
    function getShowTime(index: number): showTimeType<Moment> {
      if (typeof showTime === "object") {
        if (Array.isArray(showTime.defaultValue)) {
          return { ...showTime, defaultValue: showTime.defaultValue[index] };
        }
        return showTime as showTimeType<Moment>;
      }
      return !!showTime;
    }

    /**
     * 获取左右面板 range
     */
    function getRange(range: any): [RangeDateType, RangeDateType] {
      const [rangeMin, rangeMax] = range || [null, null];
      // 增加year处理，左右面板时间不重叠
      if (calendarType === 'year') {
        const endYear = curEndView.year();
        const startNextYear = endYear - (endYear % 20);
        const yearEndPreEnd = moment().year(startNextYear - 1).endOf('year');

        const startYear = curStartView.year();
        const endPreYear = startYear - (startYear % 20) + 20 - 1;
        const yearStartNextStart = moment().year(endPreYear + 1).startOf('year');

        return [
          [rangeMin, isAfter(yearEndPreEnd, rangeMax, 'year') ? rangeMax : yearEndPreEnd],
          [isBefore(yearStartNextStart, rangeMin, 'year') ? rangeMin : yearStartNextStart, rangeMax]
        ];
      }
      const endPreEnd = moment(curEndView)
        .subtract(1, parentUnit)
        .endOf(parentUnit);
      const startNxtStart = moment(curStartView)
        .add(1, parentUnit)
        .startOf(parentUnit);

      // console.log('getRange', parentUnit, endPreEnd, curEndView, startNxtStart, curStartView);
      return [
        [rangeMin, isAfter(endPreEnd, rangeMax) ? rangeMax : endPreEnd],
        [
          isBefore(startNxtStart, rangeMin) ? rangeMin : startNxtStart,
          rangeMax,
        ],
      ];
    }

    /**
     * 获取面板默认展示时间
     * @param index 左/右面板 - 0/1
     */
    function getDefaultViewMoment(index: number): Moment {
      const { range } = props;
      // 增加year处理，左右面板时间不重叠
      if (calendarType === 'year') {
        if (!isValidRangeValue(value)) {
          // 简单判断，只按当天处理
          // eslint-disable-next-line prefer-const
          let m = moment();
          return index === 0 ? m : moment(m).add(20, parentUnit);
        }
        let [start = moment(), end = moment()] = value || [];
        if (!start) {
          start = moment();
        }
        if (!end) {
          end = moment();
        }

        if (index === 0) {
          return start;
        }
        const startYear = start.year();
        const endYear = end.year();

        const cstart = startYear - (startYear % 20);
        const cend = cstart + 20 - 1;

        return endYear > cend ? end : moment(end).add(20, parentUnit);
      }
      const showTime = getShowTime(index);
      const time =
        showTime && typeof showTime === "object"
          ? showTime.defaultValue
          : undefined;

      // 没有初始值
      if (!isValidRangeValue(value)) {
        let m;
        if (!time) {
          m = moment();
        } else {
          m = moment(getHourMinuteSecond(time as Moment));
        }
        // range 判断
        if (Array.isArray(range)) {
          let [start, end] = range;
          if (!moment.isMoment(start)) {
            start = moment(0);
          }
          if (!moment.isMoment(end)) {
            end = moment(2 ** 52);
          }
          if (isBefore(end, m, parentUnit)) {
            return index === 0 ? moment(end).subtract(1, parentUnit) : end;
          }
          if (isAfter(start, m, parentUnit)) {
            return index === 0 ? start : moment(start).add(1, parentUnit);
          }
          if (moment.isMoment(start) && isSame(start, m, parentUnit)) {
            return index === 0 ? m : moment(m).add(1, parentUnit);
          }
        }
        return index === 0 ? moment(m).subtract(1, parentUnit) : m;
      }

      // 初始值在同个月/年
      if (isSame(value[0], value[1], parentUnit)) {
        // 根据 range 判断
        if (Array.isArray(range)) {
          // 当前月前一月不可选时，向后显示一月
          if (
            moment.isMoment(range[0]) &&
            isSame(range[0], value[1], parentUnit)
          ) {
            return index === 0 ? value[1] : moment(value[1]).add(1, parentUnit);
          }
        }
        return index === 0
          ? moment(value[1]).subtract(1, parentUnit)
          : value[1];
      }

      return value[index];
    }

    /**
     * 是否改变当前视图时间
     * 连续两月情况下，点击中间交错日期不改变当前视图时间
     * @param from 当前视图变更事件来源
     * @param nextDate 要变更到的时间
     * @param type 当前视图类型
     */
    function shouldCurViewChange(
      from: string | undefined,
      nextDate: Moment,
      type: "start" | "end" = "start"
    ): boolean {
      if (from !== "outside-date") {
        return true;
      }
      const diffMonths = (start: moment.Moment, end: moment.Moment) => {
        const years = end.year() - start.year();
        return end.month() - start.month() + years * 12;
      };
      if (diffMonths(curStartView, curEndView) === 1) {
        if (type === "start") {
          return diffMonths(curStartView, nextDate) !== 1;
        }
        return diffMonths(nextDate, curEndView) !== 1;
      }
      return true;
    }

    function handleStartInputChange(content: string, context: any) {
      setInputValue({
        ...(inputValue as RangeInputValue),
        inputValueStart: content,
      });
      let startValue = moment(content, format, true);
      let endValue = moment(
        (inputValue as RangeInputValue).inputValueEnd,
        format,
        true
      );
      let temp;
      if (!startValue.isValid()) {
        onInputValueChange(content, { valid: false });
        return;
      }

      const isValid = calendarType === "month" ? isValidMonth : isValidDate;
      const valid = isValid(startValue, props as any, endValue);
      const validTime = showTime
        ? isValidTimeValue(startValue, {
          ...showTime,
          ...props,
          ...props.disabledTime?.([startValue, endValue], "start"),
        })
        : true;
      if (valid && validTime) {
        // 输入的开始日期大于结束日期互换
        if (startValue.isAfter(endValue, "d")) {
          temp = endValue;
          endValue = startValue;
          startValue = temp;
        }
        setCurValue([startValue, endValue]);
        // todo 直接同步input的值
        if (syncInput) {
          onChange([startValue, endValue], context);
        }
      }
      setInputChange(true);
      onInputValueChange(content, { valid: false });
    }

    function handleEndInputChange(content: string, context: any) {
      setInputValue({
        ...(inputValue as RangeInputValue),
        inputValueEnd: content,
      });
      let startValue = moment(
        (inputValue as RangeInputValue).inputValueStart,
        format,
        true
      );
      let endValue = moment(content, format, true);
      let temp;
      if (!endValue.isValid()) {
        onInputValueChange(content, { valid: false });
        return;
      }

      const isValid = calendarType === "month" ? isValidMonth : isValidDate;
      const valid = isValid(endValue, props as any, startValue);
      const validTime = showTime
        ? isValidTimeValue(endValue, {
          ...showTime,
          ...props,
          ...props.disabledTime?.([startValue, endValue], "end"),
        })
        : true;
      if (valid && validTime) {
        // 输入的结束日期小于开始日期互换
        if (endValue.isBefore(startValue, "d")) {
          temp = endValue;
          endValue = startValue;
          startValue = temp;
        }
        const value = [startValue, endValue];
        setCurValue(value as any);
        // todo 直接同步input的值
        if (syncInput) {
          onChange(value, context);
        }
      }

      setInputChange(true);
      onInputValueChange(content, { valid: false });
    }

    function handleKeyDown(event: React.KeyboardEvent) {
      if (!active) {
        handleOpen();
        return;
      }
      switch (event.key) {
        case KeyMap.Enter:
          handleOk(event);
          break;
        case KeyMap.Esc:
          handleClose();
          break;
      }
    }

    const hasHeader = !!header;
    // 根据hiddenHeaders配置 过滤 header
    const filteredHeader = (() => {
      const hiddenHeaderBtns = props?.hiddenHeaderBtns;
      if (!Array.isArray(header)) return [];
      if (!hiddenHeaderBtns?.length) return header;
      return header.filter((item: any) => !hiddenHeaderBtns?.includes(item?.key));
    })();

    return (
      <Popover
        trigger={[
          DatePickerTrigger,
          { onOpen: handleOpen, onClose: handleClose },
        ]}
        visible={active}
        onVisibleChange={setActive}
        placement={placement}
        placementOffset={placementOffset}
        closeOnScroll={closeOnScroll}
        escapeWithReference={escapeWithReference}
        popupContainer={popupContainer}
        overlayClassName={overlayClassName}
        overlayStyle={overlayStyle}
        overlay={
          overlay || (<RangePickerContext.Provider value={{ range: restProps.range }}>
            <DropdownBox preventMouseDown adaptable>
              <CalendarPart.Panel className={hasHeader ? 'dtb-rangepicker-with-header' : ''} rangeMode timeMode={type[0] === "time"}>
                {hasHeader && (
                  <CalendarPart.Header>{header}</CalendarPart.Header>
                )}
                <CalendarWrapper
                  type={type}
                  {...restProps}
                  showTime={showTime}
                  value={curValue}
                  onChange={handleChange}
                  onOk={handleOk}
                  autoRange={autoRange}
                  onTypeChange={types =>
                    setType(types as [CalendarTableType, CalendarTableType])
                  }
                >
                  {({ type, onTypeChange, range, ...props }) => {
                    const [startRange, endRange] = getRange(range);
                    return (
                      <>
                        <CalendarTable
                          {...props}
                          calendarType={calendarType}
                          monthOnly={calendarType === "month"}
                          rangeType="start"
                          range={startRange}
                          type={type[0]}
                          onTypeChange={t => onTypeChange([t, type[1]])}
                          showTime={getShowTime(0)}
                          curViewMoment={curStartView}
                          onCurViewMomentChange={(date, { from } = {}) => {
                            if (!shouldCurViewChange(from, date)) {
                              return;
                            }
                            if (calendarType === 'year') {
                              const { endYear } = getStartAndEndYear(date);
                              if (curEndView.year() <= endYear) {
                                setEndCurView(
                                  moment(date).add(20, parentUnit)
                                );
                              }
                              setStartCurView(date);
                              return;
                            }
                            if (!isAfter(curEndView, date, parentUnit)) {
                              setEndCurView(moment(date).add(1, parentUnit));
                            }
                            setStartCurView(date);
                          }}
                          dateRangeInRangePicker={range || [null, null]}
                          hovered={hovered}
                          onHoveredChange={setHovered}
                        />
                        <CalendarTable
                          {...props}
                          calendarType={calendarType}
                          monthOnly={calendarType === "month"}
                          rangeType="end"
                          range={endRange}
                          type={type[1]}
                          onTypeChange={t => onTypeChange([type[0], t])}
                          showTime={getShowTime(1)}
                          curViewMoment={curEndView}
                          onCurViewMomentChange={(date, { from } = {}) => {
                            if (!shouldCurViewChange(from, date, "end")) {
                              return;
                            }
                            if (calendarType === 'year') {
                              const { startYear } = getStartAndEndYear(date);
                              if (curStartView.year() >= startYear) {
                                setStartCurView(
                                  moment(date).subtract(20, parentUnit)
                                );
                              }
                              setEndCurView(date);
                              return;
                            }
                            if (!isBefore(curStartView, date, parentUnit)) {
                              setStartCurView(
                                moment(date).subtract(1, parentUnit)
                              );
                            }
                            setEndCurView(date);
                          }}
                          dateRangeInRangePicker={range || [null, null]}
                          hovered={hovered}
                          onHoveredChange={setHovered}
                        />
                      </>
                    );
                  }}
                </CalendarWrapper>
                {
                  footerConfig ? <CalendarPart.Footer key={'customFooter'} {...footerConfig} /> : ''
                }
                
              </CalendarPart.Panel>
            </DropdownBox>
          </RangePickerContext.Provider>)
        }
        {...(popoverProps || {})}
      >
        {label || <div
          ref={ref}
          className={classNames(
            { 'dtb-rangepicker-label-wrap': rangePrefix },
            `${classPrefix}-date${showTime ? "time" : ""}picker`,
            className,
            {
              "is-disabled": disabled,
              'is-single-label': !isRangeLabel,
            }
          )}
          style={style}
          {...mergeEventProps(props)}
        >
          <div
            className={classNames(
              `${classPrefix}-date${showTime ? "time" : ""}picker__input`,
              `${classPrefix}-datepicker__input--range`,
              `${classPrefix}-datepicker__input--${calendarType === 'month' ? "month" : 'other'}`,
              {
                "is-hover": hover && clearable && isValidRangeValue(value),
                [`${classPrefix}-datetimepicker__input--millisecond`]:
                  showTime && showMilliseconds,
              }
            )}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
          >
            {rangePrefix}
            <Input
              ref={startInputRef}
              disabled={disabled}
              placeholder={
                Array.isArray(placeholder) ? placeholder[0] : placeholder
              }
              value={
                typeof inputValue === "string"
                  ? inputValue
                  : inputValue.inputValueStart
              }
              onChange={handleStartInputChange}
              onKeyDown={handleKeyDown}
              onFocus={() =>
                calendarType === "quarter" && startInputRef.current?.blur()
              }
              onClick={(event: React.MouseEvent) => {
                if (active) {
                  event.stopPropagation();
                }
              }}
            />

            {/* 范围类型日期 或 单周时，才展示 分割符 和 第二个input框 */}
            {isRangeLabel && (
              <>
                {typeof inputValue !== "string" && inputValue.separator ? (
                  <span>{inputValue.separator}</span>
                ) : <span className="dtb-rangepicker-text">~</span>}
                <Input
                  ref={endInputRef}
                  disabled={disabled}
                  placeholder={
                    Array.isArray(placeholder) ? placeholder[1] : placeholder
                  }
                  value={
                    typeof inputValue === "string"
                      ? inputValue
                      : inputValue.inputValueEnd
                  }
                  onChange={handleEndInputChange}
                  onKeyDown={handleKeyDown}
                  onFocus={() =>
                    calendarType === "quarter" && endInputRef.current?.blur()
                  }
                  onClick={(event: React.MouseEvent) => {
                    if (active) {
                      event.stopPropagation();
                    }
                  }}
                />
              </>
            )}
            {clearable && !disabled && (
              <Icon
                type="dismiss"
                onClick={event => {
                  event.stopPropagation();
                  onChange([null, null], { event });
                }}
              />
            )}
          </div>
        </div>}
      </Popover>
    );
  },
  { defaultLabelAlign: "middle" }
);

RangePicker.displayName = "RangePicker";


RangePicker.displayName = "RangePicker";
